import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import { TableColumn as RIOTableColumn, TableBody, TableRow } from '@rio-cloud/rio-uikit/Table';
import classnames from 'classnames';
import type React from 'react';

import type { RioTableRow, TableActionColumnConfig, TableColumn, TableRowKey } from './types';

interface DataTableBodyProps<T extends object> {
    columns: readonly TableColumn<T>[];
    dataSource: readonly T[];
    rowKey: TableRowKey<T>;
    actionColumn?: TableActionColumnConfig<T>;
}

const resolveRowKey = <T extends object>(rowKey: TableRowKey<T>, record: T): React.Key =>
    typeof rowKey === 'function' ? rowKey(record) : ((record as unknown as RioTableRow)[rowKey] as React.Key);

const resolveCellValue = <T extends object>(record: T, column: TableColumn<T>) =>
    column.dataIndex ? (record as unknown as RioTableRow)[column.dataIndex] : undefined;

const DataTableBody = <T extends object>({ columns, dataSource, rowKey, actionColumn }: DataTableBodyProps<T>) => (
    <TableBody>
        {dataSource.map((record, index) => {
            const separatorClassName =
                index > 0 ? 'border-top-only border-style-solid border-width-1 border-color-lighter' : undefined;

            return (
                <TableRow<RioTableRow> key={resolveRowKey(rowKey, record)} {...(record as unknown as RioTableRow)}>
                    {columns.map(column => {
                        const value = resolveCellValue(record, column);

                        return (
                            <RIOTableColumn
                                key={String(column.key)}
                                columnKey={String(column.key)}
                                className={classnames(
                                    'word-break-all text-size-14 line-height-14 padding-x-15 text-color-darkest',
                                    separatorClassName,
                                    column.className
                                )}
                                horizontalAlign={column.align}
                                overflow={column.ellipsis ? 'ellipsis' : 'visible'}
                                preventRowClick={column.stopRowClickPropagation}
                                value={column.render ? column.render(value, record, index) : (value as React.ReactNode)}
                            />
                        );
                    })}
                    {actionColumn && (
                        <RIOTableColumn
                            columnKey='actions'
                            className={separatorClassName}
                            horizontalAlign='right'
                            preventRowClick
                        >
                            <ButtonDropdown
                                aria-label={actionColumn.ariaLabel?.(record) ?? 'Row actions'}
                                title={<span className='rioglyph rioglyph-option-vertical' aria-hidden='true' />}
                                bsStyle='muted'
                                iconOnly
                                noCaret
                                pullRight
                                dropup
                                usePortal
                                dropdownClassName='min-width-100'
                                disabled={actionColumn.disabled}
                                aria-disabled={actionColumn.disabled}
                                items={actionColumn.actions(record).map(action => ({
                                    id: action.id,
                                    disabled: action.disabled,
                                    value: (
                                        <span
                                            className={classnames(
                                                'text-size-14 line-height-14 display-flex align-items-center',
                                                action.variant === 'danger'
                                                    ? 'text-color-danger'
                                                    : 'text-color-primary',
                                                action.disabled && 'opacity-40 pointer-events-none'
                                            )}
                                        >
                                            {action.icon && <span className={`rioglyph ${action.icon} text-size-14`} />}
                                            <span className='margin-left-5'>{action.title}</span>
                                        </span>
                                    ),
                                    onSelect: () => action.onSelect(record),
                                }))}
                            />
                        </RIOTableColumn>
                    )}
                </TableRow>
            );
        })}
    </TableBody>
);

export default DataTableBody;
